/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { Box, Button, Typography, TextField, MenuItem } from "@mui/material";
import { useForm, Controller, FieldValues } from "react-hook-form";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import Grid from "@mui/material/Grid";

import Panel from "../../../components/Panel";
import { RootState } from "../../../store";
import { IAttendancePunch, IAttendancePunchForm } from "../../../interfaces/AttendanceInterface";
import { UserMode } from "../../../interfaces/User";
import {
  usePostAttendancePunch,
  usePatchAttendancePunch
} from "../../../hooks/query/useAttendancePunches";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";
import {
  AttendanceDutyType,
  AttendancePunchSource,
  AttendancePunchType
} from "../../../constants/attendance";
import CalendarDropdown from "../../../components/CalendarDropdown/CalendarDropdown";
import EmployeeAndSpSelect from "../../../components/Employee/EmployeeAndSpSelect";

const punchTypeOptions = Object.entries(AttendancePunchType).map(([key, value]) => ({
  label: key.toLowerCase().replace("_", " "),
  value
}));

const dutyTypeOptions = Object.entries(AttendanceDutyType).map(([key, value]) => ({
  label: key.toLowerCase().replace(/_/g, " "),
  value
}));

interface AttendancePunchFormProps {
  onClose: () => void;
  onSuccess: (data: IAttendancePunch) => void;
  punch?: Partial<IAttendancePunch> | null;
}

// eslint-disable-next-line import/prefer-default-export
export function AttendancePunchCreateEdit({
  onClose,
  onSuccess,
  punch
}: AttendancePunchFormProps): React.JSX.Element {
  const isEdit = !!punch?.id;
  const resourceCentre = useCurrentResourceCentre();

  const { mutate: postPunchMutation } = usePostAttendancePunch();
  const { mutate: patchPunchMutation } = usePatchAttendancePunch();

  // rc data
  const rcEmployees = useSelector((state: RootState) => state.resources.resourceCentreEmployees);

  const rcServiceProviders = useSelector((state: RootState) =>
    state.resources.resourceCentreServiceProviders.filter((sp: any) => sp.active)
  );

  const [selectedEmployee, setSelectedEmployee] = React.useState<any>(
    punch?.employee || punch?.serviceProvider || null
  );

  React.useEffect(() => {
    if (!selectedEmployee) {
      if (punch?.employeeId) {
        const emp = rcEmployees?.find((e: any) => e.id === punch.employeeId);
        if (emp) setSelectedEmployee(emp);
      } else if (punch?.serviceProviderId) {
        const sp = rcServiceProviders?.find((s: any) => s.id === punch.serviceProviderId);
        if (sp) setSelectedEmployee(sp);
      }
    }
  }, [punch, rcEmployees, rcServiceProviders, selectedEmployee]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<FieldValues>({
    defaultValues: {
      punchDate: moment(punch?.punchedAt || undefined).format("YYYY-MM-DD"),
      punchTime: moment(punch?.punchedAt || undefined).format("HH:mm"),
      punchType: punch?.punchType || AttendancePunchType.CHECK_IN,
      dutyType: punch?.dutyType || AttendanceDutyType.REGULAR
    }
  });

  const onSubmit = async (data: any) => {
    if (!resourceCentre?.id || !selectedEmployee) return;

    const isEmployee =
      selectedEmployee?.userAuthenticable?.[0]?.authenticable === UserMode.Employee ||
      Boolean(
        selectedEmployee &&
          "nonUserEmployee" in selectedEmployee &&
          selectedEmployee.nonUserEmployee
      );
    const isServiceProvider =
      selectedEmployee?.userAuthenticable?.[0]?.authenticable === UserMode.ServiceProvider;

    const payload: Partial<IAttendancePunchForm> = {
      resourceCentreId: resourceCentre.id,
      punchDate: data.punchDate,
      punchTime: data.punchTime,
      punchType: data.punchType,
      punchSource: punch?.punchSource || AttendancePunchSource.MANUAL,
      dutyType: data.dutyType,
      employeeId: isEmployee ? selectedEmployee.id : undefined,
      serviceProviderId: isServiceProvider ? selectedEmployee.id : undefined
    };

    if (isEdit && punch?.id) {
      patchPunchMutation(
        { id: punch.id, data: payload },
        {
          onSuccess: (response) => {
            if (response.success) {
              onSuccess(response.data);
              onClose();
            }
          }
        }
      );
    } else {
      postPunchMutation(payload, {
        onSuccess: (response) => {
          if (response.success) {
            onSuccess(response.data);
            onClose();
          }
        }
      });
    }
  };

  return (
    <Panel
      title={isEdit ? "Edit Punch Entry" : "Manual Punch Entry"}
      onClose={onClose}
      footer={
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            width: "100%"
          }}
        >
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
            {isEdit ? "Update Punch" : "Create Punch"}
          </Button>
        </Box>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        <EmployeeAndSpSelect
          selectedEmployee={selectedEmployee}
          onChange={(value) => setSelectedEmployee(value)}
          TextFieldProps={{
            label: "Select Staff",
            placeholder: "Type to search...",
            size: "small"
          }}
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="punchType"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Punch Type"
                  error={!!errors.punchType}
                  helperText={errors.punchType?.message as string}
                  slotProps={{ inputLabel: { shrink: true } }}
                >
                  {punchTypeOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{ textTransform: "capitalize" }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="dutyType"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Duty Type"
                  error={!!errors.dutyType}
                  helperText={errors.dutyType?.message as string}
                  slotProps={{ inputLabel: { shrink: true } }}
                >
                  {dutyTypeOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{ textTransform: "capitalize" }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="punchDate"
              control={control}
              rules={{ required: "Punch date is required" }}
              render={({ field }) => (
                <CalendarDropdown
                  fullwidth
                  label="Punch Date"
                  placeholder="Select Date"
                  date={field.value ? moment(field.value, "YYYY-MM-DD").toDate() : new Date()}
                  onChange={(date) => field.onChange(moment(date).format("YYYY-MM-DD"))}
                  TextFieldProps={{
                    margin: "none",
                    variant: "outlined",
                    size: "small",
                    error: !!errors.punchDate,
                    helperText: errors.punchDate?.message as string
                  }}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="punchTime"
              control={control}
              rules={{ required: "Punch time is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Punch Time"
                  type="time"
                  error={!!errors.punchTime}
                  helperText={errors.punchTime?.message as string}
                  onFocus={(event: any) => event.target.select()}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
            />
          </Grid>
        </Grid>

        <Typography variant="caption" color="text.secondary">
          Note: Recording an attendance punch on behalf of an employee will be flagged as a
          &quot;manual&quot; override.
        </Typography>
      </Box>
    </Panel>
  );
}
